// The widget markup is rendered server-side by snippets/eck-chat-widget.liquid.
// This script holds behavior only: config (Worker endpoint, support-email error
// copy, greeting) arrives via data-* attributes on <eck-chat-widget>, so there
// are no hardcoded endpoints, emails, or UI strings here.
if (!customElements.get("eck-chat-widget")) {
  customElements.define(
    "eck-chat-widget",
    class EckChatWidget extends HTMLElement {
      constructor() {
        super();
        this.config = {
          apiEndpoint: this.dataset.apiEndpoint,
          storageKey: "eck_chat_history",
          maxHistoryLength: 20,
        };
        this.strings = {
          greeting: this.dataset.greeting || "",
          errorConnecting: this.dataset.errorConnecting || "",
          openLabel: this.dataset.openLabel || "",
          closeLabel: this.dataset.closeLabel || "",
        };
        this.isOpen = false;
        this.isLoading = false;
        this.warmed = false;
        this.conversationHistory = [];
        this._initialized = false;

        this.storage = window.EckUtils.safeStorage("local");

        // element refs scoped to this subtree (replaces the getElementById calls)
        // #eck-chat-label is the sole launcher — the round #eck-chat-toggle it used
        // to sit beside was removed (owner decision 2026-08-03).
        this.label = this.querySelector("#eck-chat-label");
        this.window = this.querySelector("#eck-chat-window");
        this.iconChat = this.querySelector("#eck-icon-chat");
        this.iconClose = this.querySelector("#eck-icon-close");
        this.messages = this.querySelector("#eck-chat-messages");
        this.input = this.querySelector("#eck-chat-input");
        this.sendBtn = this.querySelector("#eck-chat-send");
        this.clearBtn = this.querySelector("#eck-clear-chat");
        this._typingEl = null;

        // bound handlers so add/removeEventListener pair correctly
        this._onToggle = this.toggleChat.bind(this);
        this._onWarm = this.warmConnection.bind(this);
        this._onClear = this.clearChat.bind(this);
        this._onSendClick = this._handleSendClick.bind(this);
        this._onInputKey = this._handleInputKey.bind(this);
      }

      connectedCallback() {
        if (!this._initialized) {
          this.loadHistory();
          this.renderHistory();
          this._initialized = true;
        }
        this.label.addEventListener("click", this._onToggle);
        this.label.addEventListener("mouseenter", this._onWarm, { once: true });
        this.label.addEventListener("focus", this._onWarm, { once: true });
        this.label.addEventListener("touchstart", this._onWarm, { once: true, passive: true });
        this.clearBtn.addEventListener("click", this._onClear);
        this.sendBtn.addEventListener("click", this._onSendClick);
        this.input.addEventListener("keypress", this._onInputKey);
      }

      disconnectedCallback() {
        this.label.removeEventListener("click", this._onToggle);
        this.clearBtn.removeEventListener("click", this._onClear);
        this.sendBtn.removeEventListener("click", this._onSendClick);
        this.input.removeEventListener("keypress", this._onInputKey);
        // the warm listeners are { once: true }; if not yet fired, drop them too
        this.label.removeEventListener("mouseenter", this._onWarm);
        this.label.removeEventListener("focus", this._onWarm);
        this.label.removeEventListener("touchstart", this._onWarm);
      }

      // Warm the worker connection on first user intent (hover/focus/touch of the
      // pill) instead of a head preconnect — the worker is only contacted when a
      // message is actually sent, so an unconditional preconnect would idle out.
      warmConnection() {
        if (this.warmed || !this.config.apiEndpoint) return;
        this.warmed = true;
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = this.config.apiEndpoint;
        link.crossOrigin = "anonymous";
        document.head.appendChild(link);
      }

      loadHistory() {
        const saved = this.storage ? this.storage.read(this.config.storageKey, []) : [];
        this.conversationHistory = Array.isArray(saved) ? saved : [];
      }

      saveHistory() {
        if (!this.storage) return;
        this.storage.write(this.config.storageKey, this.conversationHistory.slice(-this.config.maxHistoryLength));
      }

      // Renders the ONLY rich syntax the bot is allowed to emit: markdown links
      // [label](url). url is restricted to http/https/mailto (and site-relative
      // paths) so a hostile reply can't inject a javascript:/data: anchor; the
      // label is HTML-escaped. Anything outside this subset stays plain text.
      parseMarkdownLinks(text) {
        const escapeHtml = (s) =>
          s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
        const isSafeHref = (raw) => {
          const href = String(raw || "").trim();
          if (href.startsWith("/")) return href;
          try {
            const url = new URL(href);
            if (url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:") {
              return href;
            }
          } catch (e) {}
          return null;
        };
        return escapeHtml(text).replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, rawUrl) => {
          const safe = isSafeHref(rawUrl);
          if (!safe) return match;
          const ext = /^https?:\/\//i.test(safe) ? ' target="_blank" rel="noopener noreferrer"' : "";
          return `<a href="${safe}"${ext} style="color: var(--eck-chat-accent); text-decoration: underline; cursor: pointer;">${label}</a>`;
        });
      }

      addMessage(content, isUser) {
        const div = document.createElement("div");
        div.className = `eck-message ${isUser ? "eck-user" : "eck-bot"}`;
        if (isUser) {
          div.textContent = content;
        } else {
          div.innerHTML = this.parseMarkdownLinks(content);
        }
        this.messages.appendChild(div);
        this.messages.scrollTop = this.messages.scrollHeight;
        this.conversationHistory.push({ role: isUser ? "user" : "assistant", content });
        this.saveHistory();
      }

      showTyping() {
        const div = document.createElement("div");
        div.id = "eck-typing";
        div.className = "eck-message eck-bot";
        div.innerHTML = '<span class="eck-dot"></span><span class="eck-dot"></span><span class="eck-dot"></span>';
        this.messages.appendChild(div);
        this.messages.scrollTop = this.messages.scrollHeight;
        this._typingEl = div;
      }

      hideTyping() {
        if (this._typingEl) {
          this._typingEl.remove();
          this._typingEl = null;
        }
      }

      async sendMessage(message) {
        if (this.isLoading || !message.trim()) return;
        this.isLoading = true;
        this.addMessage(message, true);
        this.showTyping();
        this.sendBtn.disabled = true;

        try {
          const response = await fetch(this.config.apiEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message, history: this.conversationHistory.slice(0, -1).slice(-10) }),
          });
          const data = await response.json();
          this.hideTyping();
          this.addMessage(data.error || data.reply, false);
        } catch (e) {
          this.hideTyping();
          this.addMessage(this.strings.errorConnecting, false);
        }

        this.isLoading = false;
        this.sendBtn.disabled = false;
        this.input.focus();
      }

      toggleChat() {
        this.isOpen = !this.isOpen;
        this.window.style.display = this.isOpen ? "flex" : "none";
        this.iconChat.style.display = this.isOpen ? "none" : "block";
        this.iconClose.style.display = this.isOpen ? "block" : "none";
        this.label.setAttribute("aria-expanded", String(this.isOpen));
        this.label.setAttribute("aria-label", this.isOpen ? this.strings.closeLabel : this.strings.openLabel);
        if (this.isOpen) {
          this.warmConnection();
          if (this.conversationHistory.length === 0) {
            this.addMessage(this.strings.greeting, false);
          }
        }
      }

      clearChat() {
        this.conversationHistory = [];
        if (this.storage) this.storage.remove(this.config.storageKey);
        this.messages.innerHTML = "";
        this.addMessage(this.strings.greeting, false);
      }

      renderHistory() {
        this.conversationHistory.forEach((msg) => {
          const div = document.createElement("div");
          div.className = `eck-message ${msg.role === "user" ? "eck-user" : "eck-bot"}`;
          if (msg.role === "user") {
            div.textContent = msg.content;
          } else {
            div.innerHTML = this.parseMarkdownLinks(msg.content);
          }
          this.messages.appendChild(div);
        });
        this.messages.scrollTop = this.messages.scrollHeight;
      }

      _handleSendClick() {
        this.sendMessage(this.input.value);
        this.input.value = "";
      }

      _handleInputKey(e) {
        if (e.key === "Enter") {
          this.sendMessage(this.input.value);
          this.input.value = "";
        }
      }
    },
  );
}
